/** @format */

import React, {useState, useCallback, useMemo, useEffect} from 'react';

import {InputAdornment} from '@mui/material';
import {Close as CloseIcon, Search as SearchIcon} from '@mui/icons-material';
import {OnChangeFn, Row, RowSelectionState} from '@tanstack/react-table';

import {TextFieldWrap} from 'components/InputsNew/TextFieldWrap';
import {TableVirtualized} from 'components/Table';
import {TanStackTableData} from 'components/Table/core/types';

import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  SearchContainer,
  ButtonsContainer,
  TableContainer,
  StyledButton,
  StyledIconButton,
  StyledOpenButton,
  ChecklistIcon,
  RemainingCountText,
  SelectedChipsContainer,
  StyledChip,
  OpenModalContainer,
} from './styles';
import {BaseSearchModalProps} from './types';

export const buildSelectionFromIds = (ids: string[]): RowSelectionState =>
  Object.fromEntries(ids.map(id => [id, true]));

const BaseSearchModal: React.FC<BaseSearchModalProps> = ({
  columns,
  data,
  modalTitle,
  searchPlaceholder,
  confirmButtonText,
  searchByColumnIds,
  initialSelectedIds,
  maxSelections,
  getDisplayLabel,
  needToConnectMetrika = false,
  disabled = false,
  onConfirm,
  onCancel,
  open: controlledOpen,
  onClose: controlledOnClose,
}) => {
  const isDisabled = needToConnectMetrika || disabled;
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [search, setSearch] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() =>
    buildSelectionFromIds(initialSelectedIds),
  );

  const filteredData = useMemo(() => {
    if (!search) return data;

    const searchLower = search.toLowerCase();
    return data.filter(item => {
      return searchByColumnIds.some(columnId => {
        const value = item[columnId];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        if (typeof value === 'number') {
          return value.toString().includes(searchLower);
        }
        return false;
      });
    });
  }, [data, search, searchByColumnIds]);

  const selectedIds = useMemo(() => {
    return Object.keys(rowSelection).filter(id => rowSelection[id]);
  }, [rowSelection]);

  const selectedCount = selectedIds.length;
  const isLimitReached = Boolean(maxSelections && selectedCount >= maxSelections);

  const isRowSelectionDisabled = useMemo(() => {
    if (!maxSelections) return undefined;
    return (row: Row<TanStackTableData>) => {
      return selectedCount >= maxSelections && !row.getIsSelected();
    };
  }, [maxSelections, selectedCount]);

  const visibleSelectedIds = initialSelectedIds.slice(0, 2);
  const remainingCount = initialSelectedIds.length - visibleSelectedIds.length;

  const filteredIds = useMemo(() => {
    return filteredData.map(item => String(item.id || item.ID || ''));
  }, [filteredData]);

  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    updaterOrValue => {
      setRowSelection(prev => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        if (!maxSelections) return next;

        const nextSelectedCount = Object.keys(next).filter(id => next[id]).length;
        if (nextSelectedCount <= maxSelections) return next;

        // Превышен лимит: сохраняем ранее выбранные, добавляем новые по порядку данных до лимита
        const result: RowSelectionState = {};
        let count = 0;

        // 1. Сохраняем строки, которые были выбраны ранее и остались выбранными
        for (const id of Object.keys(prev)) {
          if (prev[id] && next[id] && count < maxSelections) {
            result[id] = true;
            count++;
          }
        }
        // 2. Добавляем новые строки по порядку данных (filteredIds), пока не достигнем лимита
        for (const id of filteredIds) {
          if (count >= maxSelections) break;
          if (next[id] && !prev[id]) {
            result[id] = true;
            count++;
          }
        }
        return result;
      });
    },
    [maxSelections, filteredIds],
  );

  const initialSelectedKey = initialSelectedIds.join(',');

  useEffect(() => {
    if (isControlled && controlledOpen) {
      setSearch('');
      setRowSelection(buildSelectionFromIds(initialSelectedIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, controlledOpen, initialSelectedKey]);

  const handleOpen = useCallback(() => {
    if (!isControlled) {
      setUncontrolledOpen(true);
    }
    setSearch('');
    setRowSelection(buildSelectionFromIds(initialSelectedIds));
  }, [initialSelectedIds, isControlled]);

  const handleClose = useCallback(() => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setUncontrolledOpen(false);
    }
    setSearch('');
    onCancel?.();
  }, [onCancel, isControlled, controlledOnClose]);

  const handleSelectAll = useCallback(() => {
    const newSelection: RowSelectionState = {...rowSelection};
    const currentCount = Object.keys(newSelection).filter(id => newSelection[id]).length;
    let remaining = maxSelections ? maxSelections - currentCount : filteredIds.length;

    for (const id of filteredIds) {
      if (remaining <= 0) break;
      if (!newSelection[id]) {
        newSelection[id] = true;
        remaining--;
      }
    }
    setRowSelection(newSelection);
  }, [filteredIds, rowSelection, maxSelections]);

  const handleDeselectAll = useCallback(() => {
    setRowSelection({});
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm?.(selectedIds);
    setIsSubmitted(true);
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setUncontrolledOpen(false);
    }
    setSearch('');
  }, [selectedIds, onConfirm, isControlled, controlledOnClose]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  // Проверка есть ли изменения относительно изначального состояния
  const hasChanges = useMemo(() => {
    const currentSelectedSet = new Set(selectedIds);
    const initialSelectedSet = new Set(initialSelectedIds);

    if (currentSelectedSet.size !== initialSelectedSet.size) {
      return true;
    }

    for (const id of currentSelectedSet) {
      if (!initialSelectedSet.has(id)) {
        return true;
      }
    }

    return false;
  }, [selectedIds, initialSelectedIds]);

  return (
    <>
      {!isControlled && (
        <OpenModalContainer>
          <StyledOpenButton onClick={handleOpen} disabled={isDisabled}>
            <ChecklistIcon />
            Выбрать
          </StyledOpenButton>
          {initialSelectedIds.length > 0 && `${initialSelectedIds.length} из ${data.length}`}
        </OpenModalContainer>
      )}
      {!isControlled && isSubmitted && (
        <SelectedChipsContainer>
          {visibleSelectedIds.map(itemId => {
            const option = data.find(opt => String(opt.id) === itemId);
            const label = option ? getDisplayLabel(option) : itemId;
            return <StyledChip key={itemId} label={label} size='small' title={label} />;
          })}
          {remainingCount > 0 && <RemainingCountText>и ещё {remainingCount}</RemainingCountText>}
        </SelectedChipsContainer>
      )}

      <StyledDialog open={open} onClose={handleClose} maxWidth={false}>
        <StyledDialogTitle>
          {modalTitle}
          <StyledIconButton aria-label='закрыть' onClick={handleClose}>
            <CloseIcon />
          </StyledIconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          <SearchContainer>
            <TextFieldWrap
              fullWidth
              placeholder={searchPlaceholder}
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </SearchContainer>

          <ButtonsContainer>
            <StyledButton
              onClick={handleSelectAll}
              disabled={filteredData.length === 0 || isLimitReached}
            >
              {maxSelections && filteredData.length > maxSelections
                ? `Выбрать первых ${maxSelections} из ${filteredData.length}`
                : `Выбрать показанные (${filteredData.length})`}
            </StyledButton>
            <StyledButton onClick={handleDeselectAll} disabled={selectedIds.length === 0}>
              {selectedIds.length > 0
                ? `Снять выделение (${selectedIds.length})`
                : 'Снять выделение'}
            </StyledButton>
          </ButtonsContainer>

          <TableContainer>
            <TableVirtualized
              columns={columns}
              data={filteredData}
              search=''
              enableSorting
              rowSelection={rowSelection}
              setRowSelection={handleRowSelectionChange}
              isRowSelectionDisabled={isRowSelectionDisabled}
              tableHeight={400}
              variant='light'
            />
          </TableContainer>

          <StyledDialogActions>
            <StyledButton
              onClick={handleConfirm}
              disabled={selectedIds.length === 0 && !hasChanges}
              isSubmit
            >
              {selectedIds.length > 0
                ? maxSelections
                  ? `Подтвердить (${selectedIds.length})`
                  : 'Подтвердить'
                : confirmButtonText}
            </StyledButton>
            {selectedIds.length > 0 && (
              <StyledButton onClick={handleClose}>Отменить изменения</StyledButton>
            )}
          </StyledDialogActions>
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
};

export default BaseSearchModal;
